import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useNotifier } from "../../../shared/src/common/useNotifier";

import type { Invoice } from "../types/CommercialDocumentTypes";
import { ModifyInvoiceItemInput, useUpdateDocumentDraftMutation } from "@repo/server-api";

type UseInvoiceOptions = {
	onSaved: (invoice: Invoice) => void;
};

type UseInvoiceResult = {
	updateInvoice: (invoice: Invoice) => void;
	loading: boolean;
};

// Here is the hook
export function useUpdateInvoiceHook(options: UseInvoiceOptions): UseInvoiceResult {
	const { t } = useTranslation();
	const { notifySuccess, notifyException } = useNotifier();

	const { onSaved } = options;
	const [loading, setLoading] = useState(false);

	const [updateInvoiceMutation] = useUpdateDocumentDraftMutation();

	const updateInvoice = async (invoice: Invoice) => {
		setLoading(true);
		try {
			await updateInvoiceMutation({
				variables: {
					clientContactPersonId: invoice.contactPerson?.id || null,
					clientId: invoice.client?.id || null,
					concludingText: invoice.footerNote,
					contractorId: invoice.contractor?.id || null,
					documentId: invoice.id!,
					introductoryText: invoice.headerNote || "",
					items: invoice.items.map((item, index) => ({
						description: item.description,
						discountPercent: item.discount ?? 0,
						name: item.name,
						quantity: item.quantity,
						tax: item.tax,
						taxCode: item.taxCode,
						unit: item.unit,
						unitPriceNet: item.unitPrice,
						positionNo: index,
					})) as ModifyInvoiceItemInput[],
					notes: invoice.notes || "",
					paymentDeadlineInDays: invoice.paymentDeadlineDays || 30,
					projectId: invoice.project?.id || null,
					isProforma: invoice.isProforma || false,
				},
			});

			notifySuccess(t("invoice-updated"));
			onSaved(invoice);
		} catch (error) {
			notifyException(error);
		} finally {
			setLoading(false);
		}
	};

	return {
		updateInvoice,
		loading,
	};
}
